import { useState } from 'react';

export type UseStepperOptions = {
  /** Index to start on. Clamped to the valid range. Defaults to `0`. */
  initialStep?: number;
};

export type UseStepperResult = {
  /** Zero-based index of the active step. */
  step: number;

  /** Total number of steps. */
  steps: number;

  /** Label of the active step. */
  label: string;

  /** All step labels, in order. */
  labels: string[];

  /** Jump to a step by index. Clamped to the valid range; never validates or blocks. */
  goTo: (index: number) => void;

  /** Advance one step. No-op on the last step. */
  next: () => void;

  /** Go back one step. No-op on the first step. */
  back: () => void;

  /** `true` when on the first step. */
  isFirst: boolean;

  /** `true` when on the last step. */
  isLast: boolean;
};

/**
 * Headless state machine for multi-step / wizard flows. Tracks the active step index and
 * exposes free navigation (`goTo`/`next`/`back`) — no validation, no gating. Pair it with
 * `Sdk.Components.Stepper` for the default wizard chrome, or drive custom step UI directly.
 *
 * @param steps Ordered step labels.
 * @example
 * const { step, steps, label, next, back, isFirst, isLast } = Sdk.hooks.useStepper([
 *   'General', 'Education', 'Review'
 * ]);
 */
export function useStepper(
  steps: readonly string[],
  options: UseStepperOptions = {}
): UseStepperResult {
  const count = steps.length;

  const clamp = (index: number) => {
    if (count === 0) {
      return 0;
    }
    return Math.min(Math.max(index, 0), count - 1);
  };

  const [rawStep, setStep] = useState(() => clamp(options.initialStep ?? 0));

  // Derive the in-range index every render so a shrinking step list never leaves us
  // pointing past the end; navigation handlers re-clamp against the live count too.
  const step = clamp(rawStep);

  const goTo = (index: number) => setStep(clamp(index));
  const next = () => setStep((current) => clamp(current + 1));
  const back = () => setStep((current) => clamp(current - 1));

  const labels = [...steps];

  return {
    step,
    steps: count,
    label: steps[step] ?? '',
    labels,
    goTo,
    next,
    back,
    isFirst: step <= 0,
    isLast: count === 0 || step >= count - 1
  };
}
